/**
 * Profile/1D canvas workspace host (GVW-070).
 *
 * Mounts at <main> when useViewerStore.mode === 'profile' or URL hash
 * contains #mode=profile. Uses deterministic local fixture data
 * (explicitly labelled fixture) since backend profile service BE-122
 * is not yet deployed.
 *
 * Canvas background is always light per DESIGN_POLICY.md §5.
 */

import { useCallback, useMemo, useState } from 'react';
import { tokens } from '@/design';
import { PROFILE_FIXTURE } from './fixtures';
import { ProfileToolbar } from './ProfileToolbar';
import { ProfileStationsPanel } from './ProfileStationsPanel';
import { ProfileAnomalyDetail } from './ProfileAnomalyDetail';
import { ProfileComparePanel } from './ProfileComparePanel';
import { ProfileCursorReadout } from './ProfileCursorReadout';

export function ProfileCanvas() {
  const fixture = PROFILE_FIXTURE;
  const [exaggeration, setExaggeration] = useState(fixture.exaggeration);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const selectedStation = useMemo(
    () => fixture.stations.find((s) => s.id === selectedStationId) ?? null,
    [fixture.stations, selectedStationId],
  );

  const handleSelectStation = useCallback((id: string) => {
    setSelectedStationId(id);
  }, []);

  const handleExport = useCallback(() => {
    // GVW-076: engages usePluginStore.engage('export', { source: 'profile' })
  }, []);

  const handleAnnotate = useCallback(() => {
    // GVW-075: engages usePluginStore.engage('annotation', { source: 'profile' })
  }, []);

  const handleCompare = useCallback(() => {
    setCompareOpen((prev) => !prev);
  }, []);

  const handleSelectAnomaly = useCallback(
    (id: string) => {
      const anomaly = fixture.anomalies.find((a) => a.id === id);
      if (!anomaly) return;
      const station = fixture.stations.find((s) => s.chainage === anomaly.station);
      if (station) setSelectedStationId(station.id);
    },
    [fixture.anomalies, fixture.stations],
  );

  return (
    <section
      role="region"
      aria-labelledby="gvw-070-title"
      aria-describedby="gvw-070-desc"
      data-testid="profile-canvas-host"
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        gridTemplateColumns: `${tokens.layout.leftRail}px 1fr`,
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <h2
        id="gvw-070-title"
        style={{
          position: 'absolute',
          width: tokens.visuallyHidden.size,
          height: tokens.visuallyHidden.size,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        Profile workspace
      </h2>
      <div
        id="gvw-070-desc"
        role="region"
        aria-live="polite"
        aria-atomic={false}
        style={{
          position: 'absolute',
          width: tokens.visuallyHidden.size,
          height: tokens.visuallyHidden.size,
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {fixture.label}. {fixture.stations.length} stations.{' '}
        {fixture.anomalies.length} anomalies. Exaggeration {exaggeration}×.
      </div>

      {/* Toolbar — spans both columns */}
      <div style={{ gridColumn: '1 / -1' }}>
        <ProfileToolbar
          exaggeration={exaggeration}
          onExaggerationChange={setExaggeration}
          onExport={handleExport}
          onAnnotate={handleAnnotate}
          onCompare={handleCompare}
        />
      </div>

      {/* Left rail: stations + anomalies */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRight: tokens.border.thin,
        }}
      >
        <ProfileStationsPanel
          stations={fixture.stations}
          selectedStationId={selectedStationId}
          onSelectStation={handleSelectStation}
        />
        <ProfileAnomalyDetail
          anomalies={fixture.anomalies}
          onSelectAnomaly={handleSelectAnomaly}
        />
      </div>

      {/* Main canvas area */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Profile visualization canvas — always-light per DESIGN_POLICY.md §5 */}
        <div
          data-testid="profile-viz-canvas"
          aria-label="Profile 1D visualization"
          style={{
            flex: 1,
            background: tokens.color.canvas.light,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* SVG-based profile trace (fixture) */}
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${(fixture.stations.length - 1) * 25} 200`}
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0 }}
            aria-hidden="true"
          >
            {fixture.tracks
              .filter((t) => t.visible)
              .map((track) => {
                const points = track.data
                  .map((d) => `${d.x},${200 - ((d.y - 80) * exaggeration) / 1}`)
                  .join(' ');
                return (
                  <polyline
                    key={track.id}
                    points={points}
                    fill="none"
                    stroke={track.color}
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
            {selectedStation && (
              <line
                x1={selectedStation.chainage}
                y1={0}
                x2={selectedStation.chainage}
                y2={200}
                stroke={tokens.color.accent.primary}
                strokeWidth="1"
                strokeDasharray="4 2"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>

          {/* Compare panel overlay */}
          {compareOpen && (
            <div
              style={{
                position: 'absolute',
                top: tokens.space[2],
                right: tokens.space[2],
                zIndex: tokens.zIndex.panel,
              }}
            >
              <ProfileComparePanel
                activeProfileLabel={fixture.label}
                onPickSecondProfile={() => {}}
              />
            </div>
          )}
        </div>

        {/* Bottom-bar: cursor readout */}
        <ProfileCursorReadout
          station={selectedStation?.label ?? null}
          chainage={selectedStation?.chainage ?? null}
          elevation={selectedStation?.elevation ?? null}
          units={fixture.axis.units}
          exaggeration={exaggeration}
          stats={fixture.stats}
        />
      </div>
    </section>
  );
}
